import { useEffect, useState } from 'react';

import { Box, Flex, Grid, Popover, PopoverContent, PopoverTrigger, useBoolean } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { endOfMonth, format, getMonth, getYear, isAfter, isBefore, startOfMonth } from 'date-fns';

import { ArrowLeft, ArrowRight, ChevronDown } from 'assets/assetsV1/iconComponents';

import { isMobileView } from 'constants/isMobileView';

import { Button } from 'uiToolkitV1/Button';
import { TextBodyMedium, TextLabelSmall } from 'uiToolkitV1/Typography';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const isSameYearMonth = (a, b) => getYear(a) === getYear(b) && getMonth(a) === getMonth(b);

const isMonthOutOfBounds = (cellDate, minDate, maxDate) => {
  if (minDate && isBefore(endOfMonth(cellDate), startOfMonth(minDate))) return true;
  if (maxDate && isAfter(startOfMonth(cellDate), endOfMonth(maxDate))) return true;
  return false;
};

const MonthYearSelect = ({ label, value, placeholder, onSelect, minDate, maxDate, isMonthDisabled }) => {
  const [isOpen, setIsOpen] = useBoolean();
  const [viewYear, setViewYear] = useState(value ? getYear(value) : getYear(new Date()));

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset view only when popover opens; ignore later value changes mid-open
  useEffect(() => {
    if (isOpen) {
      setViewYear(value ? getYear(value) : getYear(new Date()));
    }
  }, [isOpen]);

  const minYear = minDate ? getYear(minDate) : viewYear - 100;
  const maxYear = maxDate ? getYear(maxDate) : viewYear + 100;
  const canPrevYear = viewYear > minYear;
  const canNextYear = viewYear < maxYear;

  const handleMonthClick = (monthIdx) => {
    const date = new Date(viewYear, monthIdx, 1);
    onSelect(date);
    setIsOpen.off();
  };

  const triggerLabel = value ? format(value, 'MMMM yyyy') : placeholder;

  return (
    <Popover isOpen={isOpen} placement="bottom-start" isLazy onClose={setIsOpen.off}>
      <PopoverTrigger>
        <TriggerBox onClick={setIsOpen.toggle} isOpen={isOpen}>
          <Flex flexDir="column" gap="2px" flex="1" minWidth="0">
            <TextLabelSmall color="text.g2">{label}</TextLabelSmall>
            <TextBodyMedium bold>{triggerLabel}</TextBodyMedium>
          </Flex>
          <ChevronDown
            color="text.g1"
            boxSize="16px"
            ml="8px"
            sx={{
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}
          />
        </TriggerBox>
      </PopoverTrigger>
      <PopoverContent width="280px">
        <Box p="12px">
          <Flex alignItems="center" justifyContent="space-between" mb="16px">
            <NavButton
              variant="tertiary"
              StartIcon={ArrowLeft}
              disabled={!canPrevYear}
              onClick={() => setViewYear((y) => y - 1)}
            />
            <TextBodyMedium bold>{viewYear}</TextBodyMedium>
            <NavButton
              variant="tertiary"
              StartIcon={ArrowRight}
              disabled={!canNextYear}
              onClick={() => setViewYear((y) => y + 1)}
            />
          </Flex>
          <Grid templateColumns="repeat(3, 1fr)" rowGap="12px" columnGap="8px">
            {MONTH_LABELS.map((monthLabel, idx) => {
              const cellDate = new Date(viewYear, idx, 1);
              const isSelected = value && isSameYearMonth(value, cellDate);
              const disabled = isMonthOutOfBounds(cellDate, minDate, maxDate) || !!isMonthDisabled?.(cellDate);
              return (
                <MonthCell
                  key={monthLabel}
                  isSelected={isSelected}
                  isDisabled={disabled}
                  onClick={() => {
                    if (!disabled) handleMonthClick(idx);
                  }}
                >
                  {monthLabel}
                </MonthCell>
              );
            })}
          </Grid>
        </Box>
      </PopoverContent>
    </Popover>
  );
};

const MonthYearRangePicker = ({ dateRange, setDateRange, minDate, maxDate }) => {
  const { startDate, endDate } = dateRange || {};
  const isMobile = isMobileView();

  const handleStartSelect = (date) => {
    const newStart = startOfMonth(date);
    let newEnd = endDate;
    if (!newEnd || isBefore(newEnd, newStart)) {
      newEnd = endOfMonth(date);
    } else {
      newEnd = endOfMonth(newEnd);
    }
    setDateRange({ startDate: newStart, endDate: newEnd });
  };

  const handleEndSelect = (date) => {
    const newEnd = endOfMonth(date);
    let newStart = startDate;
    if (!newStart || isAfter(newStart, newEnd)) {
      newStart = startOfMonth(date);
    } else {
      newStart = startOfMonth(newStart);
    }
    setDateRange({ startDate: newStart, endDate: newEnd });
  };

  return (
    <Flex
      p="12px"
      gap="12px"
      flexDir={isMobile ? 'column' : 'row'}
      alignItems={isMobile ? 'stretch' : 'center'}
      justifyContent="center"
    >
      <MonthYearSelect
        label="FROM"
        value={startDate}
        placeholder="Select"
        onSelect={handleStartSelect}
        minDate={minDate}
        maxDate={maxDate}
        isMonthDisabled={endDate ? (d) => isAfter(startOfMonth(d), startOfMonth(endDate)) : undefined}
      />
      <ArrowRight
        color="text.g1"
        boxSize="20px"
        flexShrink={0}
        alignSelf="center"
        transform={isMobile ? 'rotate(90deg)' : undefined}
      />
      <MonthYearSelect
        label="TO"
        value={endDate}
        placeholder="Select"
        onSelect={handleEndSelect}
        minDate={minDate}
        maxDate={maxDate}
        isMonthDisabled={startDate ? (d) => isBefore(startOfMonth(d), startOfMonth(startDate)) : undefined}
      />
    </Flex>
  );
};

const TriggerBox = styled(Flex)`
  align-items: center;
  cursor: pointer;
  border: 1px solid ${({ isOpen }) => (isOpen ? '#0D819F' : '#d0d5dd')};
  border-radius: 8px;
  padding: 12px 16px;
  flex: 1;
  min-width: 200px;
  user-select: none;
  transition: border-color 0.2s ease;
  &:hover {
    border-color: #98a2b3;
  }
`;

const MonthCell = styled(Box)`
  text-align: center;
  padding: 12px 0;
  border-radius: 6px;
  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
  font-size: 14px;
  font-weight: 600;
  user-select: none;
  color: ${({ isSelected, isDisabled, theme }) => {
    if (isSelected) return '#fff';
    if (isDisabled) return theme.colors.text.g3;
    return theme.colors.text.g1;
  }};
  background: ${({ isSelected }) => (isSelected ? '#0D819F' : 'transparent')};
  &:hover {
    background: ${({ isSelected, isDisabled }) => {
      if (isDisabled) return 'transparent';
      if (isSelected) return '#0A7795';
      return '#F5F7F9';
    }};
  }
`;

const NavButton = styled(Button)`
  svg {
    color: ${(props) => props.theme.colors.text.g1} !important;
  }
  visibility: ${(props) => (props.disabled ? 'hidden' : 'visible')};
`;

export { MonthYearRangePicker };
